// Deterministic models of one chat-completions exchange, for Lesson 3.2 ("The Model Lives
// Behind an HTTP API"). They build request and reply bodies, cut a reply at max_tokens, replay
// the gateway's failure bodies and price a feature. They never send a request.

import {
  EXAMPLE_INPUT_PRICE,
  EXAMPLE_OUTPUT_PRICE,
  inputCost,
  lessonNotes,
  noteById,
  tokenize,
  type NoteId,
} from './model-models';

export const MODEL_ID = 'zai.glm-5';

/** Cost of receiving `tokens` output tokens at the example rate, as a fixed-point string. */
export function outputCost(tokens: number) {
  return ((tokens / 1_000_000) * EXAMPLE_OUTPUT_PRICE).toFixed(6);
}
/** Input plus output cost of one exchange at the example rate, as a fixed-point string. */
export function exchangeCost(promptTokens: number, completionTokens: number) {
  return (Number(inputCost(promptTokens)) + Number(outputCost(completionTokens))).toFixed(6);
}
export const toNoteId = (value: string): NoteId =>
  lessonNotes.find((note) => String(note.id) === value)?.id ?? 1;

// --- cutting a reply at max_tokens ---------------------------------------------------------

export type FinishReason = 'stop' | 'length';
export interface Completion {
  content: string;
  completionTokens: number;
  finishReason: FinishReason;
}
/**
 * The prefix of `text` that holds its first `maxTokens` toy tokens, keeping the original
 * spacing. A real model stops emitting tokens, so the text ends wherever the last token did.
 */
export function cutText(text: string, maxTokens: number): string {
  let remaining = maxTokens;
  let end = 0;
  for (const match of text.matchAll(/\S+/g)) {
    const chunk = match[0];
    const pieces = tokenize(chunk);
    if (pieces.length >= remaining)
      return text.slice(0, match.index + pieces.slice(0, remaining).join('').length);
    remaining -= pieces.length;
    end = match.index + chunk.length;
  }
  return text.slice(0, end);
}
/** The whole text with `stop` when it fits in `maxTokens`, otherwise a cut with `length`. */
export function complete(text: string, maxTokens: number): Completion {
  const count = tokenize(text).length;
  if (count <= maxTokens) return { content: text, completionTokens: count, finishReason: 'stop' };
  return { content: cutText(text, maxTokens), completionTokens: maxTokens, finishReason: 'length' };
}

// --- api-exchange: one request body and one reply body -----------------------------------

export const SYSTEM_MESSAGE = 'List the tasks in the note, one per line, and nothing else.';
export const EXCHANGE_MAX_TOKENS = [20, 80, 200] as const;
export type ExchangeMaxTokens = (typeof EXCHANGE_MAX_TOKENS)[number];
export const toExchangeMaxTokens = (value: string): ExchangeMaxTokens =>
  EXCHANGE_MAX_TOKENS.find((size) => String(size) === value) ?? 80;
/** Each message costs a few tokens of framing on top of its content. */
export const MESSAGE_OVERHEAD = 4;
export interface ExchangeState {
  note: NoteId;
  includeSystem: boolean;
  maxTokens: ExchangeMaxTokens;
}
export const initialExchange = (): ExchangeState => ({
  note: 1,
  includeSystem: true,
  maxTokens: 80,
});
export interface ChatMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}
/** What the model answers when the system message asks for one task per line. */
const listReplies: Record<NoteId, string> = {
  1: 'Book the demo room\nSend the agenda to Ravi\nCheck the projector',
  2: 'Send the final headcount to the venue by 8 March',
  3: 'No tasks',
};
/** What the model answers when nothing tells it what shape of reply we want. */
const paragraphReplies: Record<NoteId, string> = {
  1: 'This looks like a standup note from 3 March for a launch. The demo is on the 12th and a room is still needed. Ravi wants the agenda by Friday, and Jo suspects the projector in the small room is broken, so someone should check it. The badges are already printed.',
  2: 'This is a booking confirmation from Riverside Hall for 12 March. They ask for the final headcount by 8 March and want to know whether a microphone is needed.',
  3: 'This is a thank-you note after a good week: the release note went out, the feedback was positive, and everyone is wished a good weekend.',
};
export function exchangeMessages(state: ExchangeState): ChatMessage[] {
  return [
    ...(state.includeSystem ? [{ role: 'system' as const, content: SYSTEM_MESSAGE }] : []),
    { role: 'user', content: noteById(state.note).text },
  ];
}
export const promptTokens = (messages: ChatMessage[]) =>
  messages.reduce((sum, message) => sum + tokenize(message.content).length + MESSAGE_OVERHEAD, 0);
export function requestBody(state: ExchangeState): string {
  return JSON.stringify(
    {
      model: MODEL_ID,
      messages: exchangeMessages(state),
      max_tokens: state.maxTokens,
      temperature: 0,
    },
    null,
    2,
  );
}
export interface Exchange {
  messages: ChatMessage[];
  promptTokens: number;
  completion: Completion;
}
export function exchange(state: ExchangeState): Exchange {
  const messages = exchangeMessages(state);
  const reply = state.includeSystem ? listReplies[state.note] : paragraphReplies[state.note];
  return {
    messages,
    promptTokens: promptTokens(messages),
    completion: complete(reply, state.maxTokens),
  };
}
export function replyBody(state: ExchangeState): string {
  const { promptTokens: prompt, completion } = exchange(state);
  return JSON.stringify(
    {
      id: 'chatcmpl-example',
      object: 'chat.completion',
      model: MODEL_ID,
      choices: [
        {
          index: 0,
          message: { role: 'assistant', content: completion.content },
          finish_reason: completion.finishReason,
        },
      ],
      usage: {
        prompt_tokens: prompt,
        completion_tokens: completion.completionTokens,
        total_tokens: prompt + completion.completionTokens,
      },
    },
    null,
    2,
  );
}
export type Tone = 'neutral' | 'success' | 'warning';
export interface Summary {
  message: string;
  tone: Tone;
}
export function exchangeSummary(state: ExchangeState): Summary {
  const { completion } = exchange(state);
  const count = completion.completionTokens;
  if (completion.finishReason === 'length')
    return {
      tone: 'warning',
      message: 'The reply hit max_tokens and stopped mid-sentence: finish_reason is length.',
    };
  if (state.includeSystem)
    return {
      tone: 'success',
      message: `With the system message the reply is one task per line: ${count} completion tokens.`,
    };
  return {
    tone: 'warning',
    message: `Without the system message the reply is a paragraph: ${count} completion tokens.`,
  };
}

// --- api-finish-reasons: a cut reply looks fine until you use it -------------------------

export type ReplyKind = 'sentence' | 'json';
export const replyTexts: Record<ReplyKind, { label: string; text: string }> = {
  sentence: {
    label: 'A sentence',
    text: 'Pune is a vibrant city in western India, known for its universities, its monsoon and its food.',
  },
  json: {
    label: 'A JSON list',
    text: '{"tasks": [{"title": "Book the demo room"}, {"title": "Send the agenda to Ravi"}, {"title": "Check the projector"}]}',
  },
};
export const toReplyKind = (value: string): ReplyKind =>
  value === 'sentence' ? 'sentence' : 'json';
export const FINISH_MAX_TOKENS = [5, 20, 80] as const;
export type FinishMaxTokens = (typeof FINISH_MAX_TOKENS)[number];
export const toFinishMaxTokens = (value: string): FinishMaxTokens =>
  FINISH_MAX_TOKENS.find((size) => String(size) === value) ?? 20;
export interface FinishState {
  reply: ReplyKind;
  maxTokens: FinishMaxTokens;
}
export const initialFinish = (): FinishState => ({ reply: 'json', maxTokens: 20 });
export const finishCompletion = (state: FinishState) =>
  complete(replyTexts[state.reply].text, state.maxTokens);
/** What `json.loads(content)` would do with the content the reply carries. */
export function parseOutcome(state: FinishState): string {
  if (state.reply === 'sentence') return 'not JSON';
  return finishCompletion(state).finishReason === 'stop'
    ? 'parsed: 3 tasks'
    : 'JSONDecodeError: unterminated string or list';
}
export function finishSummary(state: FinishState): Summary {
  return finishCompletion(state).finishReason === 'length'
    ? {
        tone: 'warning',
        message: 'The model stopped because max_tokens ran out, not because it finished.',
      }
    : {
        tone: 'success',
        message: 'The model finished on its own, so the content is complete.',
      };
}

// --- api-failures: which failures a program should retry ---------------------------------

export type FailureKind = '401' | '400' | '404' | '429' | '503' | 'timeout';
export type RetryPolicy = 'permanent' | 'budget' | 'transient';
export interface FailureSpec {
  kind: FailureKind;
  label: string;
  status: number | null;
  /** The short code shown on the attempt line; the body carries the full message. */
  code: string;
  body: string;
  policy: RetryPolicy;
  /** How the Result names a request that cannot be fixed by retrying, and what to fix. */
  noun: string;
  fix: string;
}
const errorBody = (message: string, type: string, code: string) =>
  JSON.stringify({ error: { message, type, code } });
export const failures: readonly [
  FailureSpec,
  FailureSpec,
  FailureSpec,
  FailureSpec,
  FailureSpec,
  FailureSpec,
] = [
  {
    kind: '401',
    label: '401 wrong token',
    status: 401,
    code: 'invalid_api_key',
    body: errorBody(
      'This workspace credential is not valid. Reopen the IDE from Mio to refresh it.',
      'authentication_error',
      'invalid_api_key',
    ),
    policy: 'permanent',
    noun: 'wrong-token',
    fix: 'token',
  },
  {
    kind: '400',
    label: '400 bad request',
    status: 400,
    code: 'invalid_messages',
    body: errorBody(
      'messages must be a non-empty array',
      'invalid_request_error',
      'invalid_messages',
    ),
    policy: 'permanent',
    noun: 'malformed',
    fix: 'request',
  },
  {
    kind: '404',
    label: '404 unknown model',
    status: 404,
    code: 'model_not_found',
    body: errorBody(
      'Only zai.glm-5 is available in Mio workspaces.',
      'invalid_request_error',
      'model_not_found',
    ),
    policy: 'permanent',
    noun: 'wrong-model',
    fix: 'model name',
  },
  {
    kind: '429',
    label: '429 daily budget used up',
    status: 429,
    code: 'daily_budget_exhausted',
    body: errorBody(
      "Today's model budget for this workspace is used up. It resets at midnight UTC.",
      'rate_limit_error',
      'daily_budget_exhausted',
    ),
    policy: 'budget',
    noun: 'budget-limited',
    fix: 'schedule',
  },
  {
    kind: '503',
    label: '503 provider unavailable',
    status: 503,
    code: 'upstream_unavailable',
    body: errorBody(
      'The model provider is unavailable. Try again shortly.',
      'server_error',
      'upstream_unavailable',
    ),
    policy: 'transient',
    noun: 'provider-side',
    fix: 'wait',
  },
  {
    kind: 'timeout',
    label: 'timeout after 30 s',
    status: null,
    code: 'ReadTimeout',
    body: 'httpx.ReadTimeout: timed out after 30 seconds',
    policy: 'transient',
    noun: 'timed-out',
    fix: 'wait',
  },
];
export const failureByKind = (kind: FailureKind): FailureSpec =>
  failures.find((failure) => failure.kind === kind) ?? failures[0];
export const toFailureKind = (value: string): FailureKind =>
  failures.find((failure) => failure.kind === value)?.kind ?? '503';
export const policyLabels: Record<RetryPolicy, string> = {
  permanent: 'do not retry',
  budget: 'wait until midnight',
  transient: 'retry with a wait, at most 4 attempts',
};
export const ATTEMPT_CAP = 4;
export const SUCCESS_BODY =
  '{"choices":[{"message":{"content":"Book the demo room"}}],"usage":{"prompt_tokens":57,"completion_tokens":5,"total_tokens":62}}';
export interface Attempt {
  attempt: number;
  waitSeconds: number;
  status: number | null;
  ok: boolean;
  outcome: string;
  body: string;
}
export interface FailuresState {
  failure: FailureKind;
  attempts: Attempt[];
}
export const initialFailures = (): FailuresState => ({ failure: '503', attempts: [] });
/** The first attempt is immediate; each retry waits twice as long as the one before. */
export const waitBefore = (attempt: number) => (attempt <= 1 ? 0 : 2 ** (attempt - 2));
export function attemptFor(kind: FailureKind, attempt: number): Attempt {
  const spec = failureByKind(kind);
  const waitSeconds = waitBefore(attempt);
  if (spec.policy === 'transient' && attempt >= 3)
    return {
      attempt,
      waitSeconds,
      status: 200,
      ok: true,
      outcome: '200 chat.completion',
      body: SUCCESS_BODY,
    };
  return {
    attempt,
    waitSeconds,
    status: spec.status,
    ok: false,
    outcome:
      spec.status === null
        ? 'no status, httpx.ReadTimeout after 30 s'
        : `${spec.status} ${spec.code}`,
    body: spec.body,
  };
}
export const lastAttempt = (state: FailuresState) => state.attempts.at(-1);
export const canRetry = (state: FailuresState) => {
  const last = lastAttempt(state);
  return last !== undefined && !last.ok && state.attempts.length < ATTEMPT_CAP;
};
export const totalWait = (state: FailuresState) =>
  state.attempts.reduce((sum, attempt) => sum + attempt.waitSeconds, 0);
export const attemptLine = (attempt: Attempt) =>
  `Attempt ${attempt.attempt}: wait ${attempt.waitSeconds} s → ${attempt.outcome}`;
/** The response as the program saw it: a status line and body, or only the exception. */
export const responseText = (attempt: Attempt) =>
  attempt.status === null ? attempt.body : `HTTP ${attempt.status}\n${attempt.body}`;
export type FailuresAction =
  | { type: 'failure'; value: FailureKind }
  | { type: 'send' }
  | { type: 'retry' }
  | { type: 'reset' };
export function failuresTransition(state: FailuresState, action: FailuresAction): FailuresState {
  switch (action.type) {
    case 'reset':
      return initialFailures();
    case 'failure':
      return { failure: action.value, attempts: [] };
    case 'send':
      return { ...state, attempts: [attemptFor(state.failure, 1)] };
    case 'retry': {
      if (!canRetry(state)) return state;
      return {
        ...state,
        attempts: [...state.attempts, attemptFor(state.failure, state.attempts.length + 1)],
      };
    }
  }
}
export function failuresSummary(state: FailuresState): Summary {
  const spec = failureByKind(state.failure);
  const last = lastAttempt(state);
  const count = state.attempts.length;
  if (!last) return { tone: 'neutral', message: 'Nothing sent yet. Press Send the request.' };
  if (last.ok) {
    const waits = state.attempts
      .slice(1)
      .map((attempt) => attempt.waitSeconds)
      .join(' and ');
    return {
      tone: 'success',
      message: `Attempt ${count} succeeded after waits of ${waits} seconds. ${
        state.failure === 'timeout'
          ? 'The timed-out request may have run and been billed.'
          : 'The provider recovered during the waits.'
      }`,
    };
  }
  if (spec.policy === 'transient')
    return {
      tone: 'warning',
      message:
        count === 1
          ? `Attempt 1 got ${last.outcome}. The request is fine, so wait and press Retry.`
          : `Attempt ${count} got ${last.outcome} again. Wait longer, then press Retry.`,
    };
  const verdict =
    spec.policy === 'budget'
      ? 'The budget resets at midnight. Retrying now changes nothing.'
      : `Retrying cannot fix a ${spec.noun} request. Fix the ${spec.fix}.`;
  if (count >= ATTEMPT_CAP)
    return {
      tone: 'warning',
      message: `Four attempts is the cap, so the program stops. ${verdict}`,
    };
  if (count === 1)
    return {
      tone: 'warning',
      message: `${last.outcome}: the gateway rejected the request. Press Retry and see whether the answer changes.`,
    };
  return { tone: 'warning', message: verdict };
}

// --- api-cost: what the feature costs per day ----------------------------------------------

export type ReplyLength = 'line' | 'paragraph';
export const replyLengths: Record<ReplyLength, { label: string; tokens: number }> = {
  line: { label: 'One line, about 15 tokens', tokens: 15 },
  paragraph: { label: 'A paragraph, about 120 tokens', tokens: 120 },
};
export const toReplyLength = (value: string): ReplyLength =>
  value === 'paragraph' ? 'paragraph' : 'line';
export const REQUESTS_PER_DAY = [10, 100, 1000] as const;
export type RequestsPerDay = (typeof REQUESTS_PER_DAY)[number];
export const toRequestsPerDay = (value: string): RequestsPerDay =>
  REQUESTS_PER_DAY.find((count) => String(count) === value) ?? 100;
/** The system message and per-message framing, on top of the note itself. */
export const PROMPT_OVERHEAD = 20;
export interface CostState {
  note: NoteId;
  reply: ReplyLength;
  perDay: RequestsPerDay;
}
export const initialCost = (): CostState => ({ note: 1, reply: 'line', perDay: 100 });
export interface CostBreakdown {
  inputTokens: number;
  outputTokens: number;
  inputCost: string;
  outputCost: string;
  perRequest: string;
  perDay: string;
  perMonth: string;
  inputShare: number;
  outputShare: number;
}
export function costBreakdown(state: CostState): CostBreakdown {
  const inputTokens = tokenize(noteById(state.note).text).length + PROMPT_OVERHEAD;
  const outputTokens = replyLengths[state.reply].tokens;
  const input = (inputTokens / 1_000_000) * EXAMPLE_INPUT_PRICE;
  const output = (outputTokens / 1_000_000) * EXAMPLE_OUTPUT_PRICE;
  const request = input + output;
  const outputShare = Math.round((output / request) * 100);
  return {
    inputTokens,
    outputTokens,
    inputCost: inputCost(inputTokens),
    outputCost: outputCost(outputTokens),
    perRequest: request.toFixed(6),
    perDay: (request * state.perDay).toFixed(4),
    perMonth: (request * state.perDay * 30).toFixed(2),
    inputShare: 100 - outputShare,
    outputShare,
  };
}
export function costSummary(state: CostState): Summary {
  const cost = costBreakdown(state);
  if (state.reply === 'paragraph')
    return {
      tone: 'warning',
      message: `The paragraph reply is ${cost.outputShare}% of the bill. Output tokens cost five times input tokens.`,
    };
  const split =
    cost.inputShare > 50
      ? `The note is most of the cost (${cost.inputShare}%).`
      : `Even a ${cost.outputTokens}-token reply is ${cost.outputShare}% of the bill.`;
  return {
    tone: 'success',
    message: `${state.perDay} requests a day cost ${cost.perDay}. ${split}`,
  };
}
